// utils/chatGptOcr.ts
// AIOCR の向き先（Gemini / ChatGPT）切替に使う ComSystemMapper の取得ヘルパー。
// 区分「UseChatGPT」の値が "true" のとき ChatGPT を使う。

import { businessCardAPI } from '../services/api';
import { indexedDBStorage } from '../services/storage/indexedDB';

/** ComSystemMapper の区分名 */
export const USE_CHATGPT_CLASSIFICATION = 'UseChatGPT';

/**
 * 撮影時に確定した緊急モード（UseChatGPT）を保持するためのキー。
 * 1回の撮影フロー（カメラ→編集→フォロー登録）の間は同じ値を使い回し、
 * カメラに再入場・メニューに戻った時にのみ取り直す。
 */
const CAPTURED_EMERGENCY_MODE_KEY = 'capturedEmergencyMode';

/** AIOCR の向き先種別。uploadbusinesscard の businesscardocrtype に送る値。 */
export const BUSINESS_CARD_OCR_TYPE = {
  /** Gemini */
  GEMINI: 1,
  /** ChatGPT */
  CHATGPT: 2,
} as const;

/**
 * ComSystemMapper から「UseChatGPT」の値（文字列）を取得する。
 * 取得できない場合は null を返す。
 */
export const getUseChatGptValue = async (): Promise<string | null> => {
  try {
    const response = await businessCardAPI.getComSystemMapperValueList(USE_CHATGPT_CLASSIFICATION);
    if (response?.statuscode === 200) {
      const list = response.comsystemdatalist;
      if (Array.isArray(list) && list.length > 0) {
        // divvalue が実値（"true"/"false"）。divvaluename は表示名なので使わない。
        return (list[0]?.divvalue ?? null) as string | null;
      }
      return null;
    }
    console.error('UseChatGPT 取得エラー:', response);
  } catch (error) {
    console.error('UseChatGPT 取得処理中にエラーが発生しました:', error);
  }
  return null;
};

/**
 * UseChatGPT が "true" なら true（ChatGPT を使う）。それ以外は false（Gemini）。
 */
export const isUseChatGpt = async (): Promise<boolean> => {
  const value = await getUseChatGptValue();
  return value === 'true';
};

/**
 * 撮影フロー開始時（カメラ入場・メニュー復帰）に緊急モードを確定して保持する。
 * 以降のページ（編集・フォロー登録）はこの保持値を参照する。
 * @returns 確定した緊急モード（true=ChatGPT/緊急）
 */
export const refreshCapturedEmergencyMode = async (): Promise<boolean> => {
  const mode = await isUseChatGpt();
  try {
    await indexedDBStorage.setItem(
      CAPTURED_EMERGENCY_MODE_KEY,
      mode ? 'true' : 'false',
    );
  } catch (error) {
    // 保存失敗は致命的ではない（フォールバックで都度取得される）
    console.error('緊急モードの保持に失敗:', error);
  }
  return mode;
};

/**
 * 保持中（撮影時に確定）の緊急モードを取得する。再取得はしない。
 * 未保持の場合のみ、フォールバックとして都度取得する（保持はしない）。
 * @returns 緊急モード（true=ChatGPT/緊急）
 */
export const getEmergencyMode = async (): Promise<boolean> => {
  try {
    const v = await indexedDBStorage.getItem(CAPTURED_EMERGENCY_MODE_KEY);
    if (v === 'true') return true;
    if (v === 'false') return false;
  } catch (error) {
    // 読めない場合はフォールバックへ
  }
  // 未保持時のフォールバック（直接フローに入った等）。保持はしない。
  return await isUseChatGpt();
};

/**
 * 送信用の businesscardocrtype（ChatGPT=2 / Gemini=1）を返す。
 * 撮影時に確定した保持値を用いて、解析エンジンと送信種別を一致させる。
 */
export const getBusinessCardOcrType = async (): Promise<number> => {
  return (await getEmergencyMode()) ? BUSINESS_CARD_OCR_TYPE.CHATGPT : BUSINESS_CARD_OCR_TYPE.GEMINI;
};
